import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, subtask, todo, type Todo } from "@listto/powersync";
import { computeNextDueDate, nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import { computeReminderAt, deriveReminderOffsetMinutes } from "./scheduling";
import { generateId } from "@fleet/utils";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). Mutations **can throw**: the caller
 * caller must propagate it, never swallow it.
 */

/**
 * Creates a new Todo.
 */
export async function createTodoMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  text: string,
  opts?: {
    listId?: string;
    important?: boolean;
    dueDate?: string;
    myDayDate?: string;
    note?: string;
    recurrenceRule?: string;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(todo).values({
    id,
    user_id: userId,
    list_id: opts?.listId ?? null,
    text: text.trim(),
    completed: 0,
    important: opts?.important ? 1 : 0,
    note: opts?.note ?? null,
    due_date: opts?.dueDate ?? null,
    my_day_date: opts?.myDayDate ?? null,
    recurrence_rule: opts?.recurrenceRule ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Updates an existing Todo.
 */
export async function updateTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    text: string | null;
    completed: number | null;
    important: number | null;
    note: string | null;
    due_date: string | null;
    recurrence_rule: string | null;
    my_day_date: string | null;
    list_id: string | null;
    reminder_at: string | null;
    assigned_to: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.text !== undefined && finalUpdates.text !== null) {
    finalUpdates.text = finalUpdates.text.trim();
  }

  await patchRowVerified(db, todo, todo.id, id, finalUpdates, options);
}

/**
 * Toggles a Todo's completion status. Handles recurrence if necessary.
 */
export async function toggleTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentCompleted: boolean,
  currentTodo?: Todo,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const completing = !currentCompleted;

  const recurrenceRule = currentTodo?.recurrence_rule;
  // Only spawn the next occurrence when there is a concrete due date to advance
  // from; a recurrence rule without a due date has no anchor and would loop on
  // `new Date()`, so we treat it as a plain completion.
  if (completing && recurrenceRule && currentTodo.due_date) {
    const nextDue = computeNextDueDate(currentTodo.due_date, recurrenceRule);
    const now = nowISO();

    // `computeNextDueDate` returns null once the recurrence hits its UNTIL bound;
    // in that case we just complete the task without scheduling a successor.
    if (nextDue === null) {
      await patchRowVerified(db, todo, todo.id, id, { completed: 1 }, options);
      return;
    }

    // Carry the relative reminder over to the next occurrence so a "10 min
    // before" reminder keeps firing on every instance, re-anchored to the new
    // due date.
    const reminderOffset = deriveReminderOffsetMinutes(
      currentTodo.due_date,
      currentTodo.reminder_at,
    );
    const nextReminderAt =
      reminderOffset === null ? null : computeReminderAt(nextDue, reminderOffset);
    const newId = generateId();

    // The transaction must NOT retry (it would sleep while holding the write lock),
    // so the wait goes OUTSIDE: if the row is not in the view, the UPDATE would
    // match nothing but the sibling INSERT would still occur —
    // leaving the original task incomplete AND its successor duplicated, both active.
    await waitForRowPresent(db, todo, todo.id, id, options);

    // Complete the current occurrence and spawn the next one atomically, so a
    // reader never observes a recurring task that is neither pending nor has a
    // successor.
    await db.transaction(async (trx) => {
      await trx.update(todo).set({ completed: 1, updated_at: now }).where(eq(todo.id, id));

      await trx.insert(todo).values({
        id: newId,
        user_id: currentTodo.user_id,
        list_id: currentTodo.list_id ?? null,
        text: currentTodo.text,
        completed: 0,
        important: currentTodo.important,
        note: currentTodo.note ?? null,
        due_date: nextDue,
        reminder_at: nextReminderAt,
        my_day_date: null,
        recurrence_rule: recurrenceRule,
        created_at: now,
        updated_at: now,
      });
    });
  } else {
    await patchRowVerified(db, todo, todo.id, id, { completed: completing ? 1 : 0 }, options);
  }
}

/**
 * Toggles a Todo's important status.
 */
export async function toggleImportantMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentImportant: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, todo, todo.id, id, { important: currentImportant ? 0 : 1 }, options);
}

/**
 * Adds or removes a Todo from My Day.
 */
export async function addToMyDayMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  include: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    todo,
    todo.id,
    id,
    { my_day_date: include ? nowISO() : null },
    options,
  );
}

/**
 * Soft-deletes a Todo (trash, ACTIVITY-CORE L2): sets `deleted_at`, and normal
 * views filter it out. Subtasks remain intact for restore.
 * Permanent deletion is `purgeTodoMutation`.
 */
export async function deleteTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, todo, todo.id, id, { deleted_at: nowISO() }, options);
}

/** Restores a task from the trash (the server records it as `restored`). */
export async function restoreTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, todo, todo.id, id, { deleted_at: null }, options);
}

/**
 * PERMANENTLY purges a task (and its subtasks) from the trash.
 * Irreversible on the client; the server preserves the snapshot in `activity`.
 */
export async function purgeTodoMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(subtask).where(eq(subtask.todo_id, id));
    await trx.delete(todo).where(eq(todo.id, id));
  });
}
